import {
  Action,
  buildReleaseVersion,
} from '@agilelab/plugin-wb-builder-common';
import {
  WbButton,
  WbDivider,
  WbCardActions,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import { makeStyles } from '@material-ui/core';
import { FC, useMemo } from 'react';
import { useReleaseDetailPageContext } from '../context/useReleaseDetailPageContext';
import { useDeployRequest } from '../../../hooks';

const useStyles = makeStyles(theme => ({
  success: {
    background: theme.palette.success.main,
    '&:hover': {
      background: theme.palette.success.main,
    },
  },
  error: {
    background: theme.palette.error.main,
    '&:hover': {
      background: theme.palette.error.main,
    },
  },
  divider: {
    display: 'none',
    '@media (min-width: 600px)': {
      display: 'block',
    },
  },
}));

export const ProvisioningCardActions: FC<{ canProvision: boolean }> = ({
  canProvision,
}) => {
  const classes = useStyles();
  const {
    isDeployingAll,
    isUndeployingAll,
    queryParamVersion,
    queryParamEnvironment,
    setIsDescriptorDrawerOpen,
    currentDescriptor,
    deploymentUnitStatusState,
    release,
    environment,
    setReleaseDetailAction,
    setIsDeploymentPreviewOpen,
  } = useReleaseDetailPageContext();

  const selectedEnvironment = queryParamEnvironment || environment.name;
  const releaseEntityName = release.metadata.name;

  const {
    loading: approvalLoading,
    showRequestDeploy,
    showWaitingForApproval,
    approverRole,
    deployButtonLabel,
    isRequestingDeploy,
    handleRequestDeploy,
  } = useDeployRequest({ releaseEntityName, environment: selectedEnvironment });

  const isCurrentlyDeployed = useMemo(() => {
    const response = deploymentUnitStatusState.value;
    const releaseVersion = queryParamVersion
      ? buildReleaseVersion({ v: queryParamVersion })
      : buildReleaseVersion({ release });
    const responseVersion = response?.provisioningDetails?.descriptorVersion;

    const sameVersion = responseVersion
      ? responseVersion?.includes(releaseVersion.split('-')[0])
      : false;

    return Boolean(sameVersion);
  }, [deploymentUnitStatusState.value, release, queryParamVersion]);

  const getDeployTooltip = () => {
    if (showWaitingForApproval) {
      const roleName = approverRole
        ?.replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      const approverMsg = roleName ? `from the ${roleName}` : '';
      return `Deploy request is pending approval ${approverMsg}`.trim();
    }
    if (isDeployingAll)
      return 'Wait for the current deployment operation to end';
    if (isUndeployingAll)
      return 'Wait for the current undeployment operation to end';
    if (Boolean(queryParamVersion))
      return 'This is a read-only mode. Please go to the latest release to deploy this version';

    if (!canProvision)
      return `You are not allowed to deploy the selected release from the selected environment`;
    if (showRequestDeploy)
      return 'Request deploy approval for the selected release';
    return 'Deploy the selected release to the selected environment. This will start a provisioning operation consisting of multiple tasks. If all the tasks are successful, the selected release will be published in the marketplace';
  };

  const getUndeployTooltip = () => {
    if (isDeployingAll)
      return 'Wait for the current deployment operation to end';
    if (isUndeployingAll)
      return 'Wait for the current undeployment operation to end';
    if (Boolean(queryParamVersion))
      return 'This is a read-only mode. Please go to the latest release to undeploy this version';

    if (!isCurrentlyDeployed) return 'This release is currently not deployed';
    if (!canProvision)
      return `You are not allowed to undeploy the selected release from the selected environment`;
    return 'Undeploy the selected release or old deployed draft release from the selected environment. This will start an unprovisioning operation consisting of multiple tasks. If all the tasks are successful, the selected release will be removed from the marketplace';
  };

  const handleDeployClick = () => {
    if (showRequestDeploy) {
      handleRequestDeploy();
    } else {
      setIsDeploymentPreviewOpen(true);
      setReleaseDetailAction(Action.DEPLOY);
    }
  };

  return (
    <WbCardActions breakpoint="w_xs">
      <WbButton
        label="Show Descriptor"
        variant="outlined"
        onClick={() => {
          setIsDescriptorDrawerOpen(true);
        }}
        disabled={currentDescriptor === ''}
      />

      <WbDivider className={classes.divider} />
      <WbTooltip title={getDeployTooltip()}>
        <span>
          <WbButton
            className={classes.success}
            label={deployButtonLabel}
            onClick={handleDeployClick}
            loading={isDeployingAll || isRequestingDeploy || approvalLoading}
            disabled={
              approvalLoading ||
              isDeployingAll ||
              isUndeployingAll ||
              showWaitingForApproval ||
              isRequestingDeploy ||
              Boolean(queryParamVersion) ||
              !currentDescriptor ||
              !canProvision
            }
            fullWidthUnderBreakpoint="w_xs"
          />
        </span>
      </WbTooltip>
      <WbTooltip title={getUndeployTooltip()}>
        <span>
          <WbButton
            className={classes.error}
            label="Undeploy"
            onClick={() => {
              setIsDeploymentPreviewOpen(true);
              setReleaseDetailAction(Action.UNDEPLOY);
            }}
            loading={isUndeployingAll}
            disabled={
              isDeployingAll ||
              isUndeployingAll ||
              Boolean(queryParamVersion) ||
              !isCurrentlyDeployed ||
              !currentDescriptor ||
              !canProvision
            }
            fullWidthUnderBreakpoint="w_xs"
          />
        </span>
      </WbTooltip>
    </WbCardActions>
  );
};
